/**
 * Validation utility functions for form inputs and data
 *
 * Validators are intentionally i18n-agnostic: they return a stable
 * translation key (`errorKey`) plus any interpolation `params`, rather than
 * a localized string. Call sites translate the result — components via the
 * `useTranslation()` hook (`t(result.errorKey, result.params)`) and
 * non-React code via the shared i18n instance (`i18n.t(...)`).
 */

export interface ValidationResult {
  isValid: boolean;
  /** i18n key for the failure message (omitted when `isValid` is true). */
  errorKey?: string;
  /** Interpolation values for the message, e.g. `{ fieldName, minLength }`. */
  params?: Record<string, string | number>;
}

/**
 * Validate email address format
 */
export const validateEmail = (email: string): ValidationResult => {
  if (!email) {
    return {
      isValid: false,
      errorKey: 'validation.email.required'
    };
  }

  if (email.length > 254) {
    return {
      isValid: false,
      errorKey: 'validation.email.tooLong'
    };
  }

  // Basic email regex pattern
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(email)) {
    return {
      isValid: false,
      errorKey: 'validation.email.invalid'
    };
  }

  return {
    isValid: true
  };
};

/**
 * Validate password requirements
 */
export const validatePassword = (password: string): ValidationResult => {
  if (!password) {
    return {
      isValid: false,
      errorKey: 'validation.password.required'
    };
  }

  if (password.length < 6) {
    return {
      isValid: false,
      errorKey: 'validation.password.tooShort'
    };
  }

  return {
    isValid: true
  };
};

/**
 * Validate return URL format and security
 */
export const validateReturnUrl = (url: string): ValidationResult => {
  if (!url) {
    return {
      isValid: false,
      errorKey: 'validation.returnUrl.required'
    };
  }

  try {
    const parsedUrl = new URL(url);

    // Check protocol
    if (parsedUrl.protocol !== 'https:' &&
        !parsedUrl.hostname.includes('localhost') &&
        parsedUrl.hostname !== '127.0.0.1') {
      return {
        isValid: false,
        errorKey: 'validation.returnUrl.mustUseHttps'
      };
    }

    // Prevent dangerous protocols
    if (parsedUrl.protocol === 'javascript:' || parsedUrl.protocol === 'data:') {
      return {
        isValid: false,
        errorKey: 'validation.returnUrl.protocolNotAllowed'
      };
    }

    return {
      isValid: true
    };
  } catch (error) {
    return {
      isValid: false,
      errorKey: 'validation.returnUrl.invalid'
    };
  }
};

/**
 * Validate required field
 */
export const validateRequired = (value: string, fieldName: string): ValidationResult => {
  if (!value || value.trim().length === 0) {
    return {
      isValid: false,
      errorKey: 'validation.field.required',
      params: { fieldName }
    };
  }

  return {
    isValid: true
  };
};

/**
 * Validate string length
 */
export const validateLength = (
  value: string,
  minLength: number,
  maxLength: number,
  fieldName: string
): ValidationResult => {
  if (value.length < minLength) {
    return {
      isValid: false,
      errorKey: 'validation.field.minLength',
      params: { fieldName, minLength }
    };
  }

  if (value.length > maxLength) {
    return {
      isValid: false,
      errorKey: 'validation.field.maxLength',
      params: { fieldName, maxLength }
    };
  }

  return {
    isValid: true
  };
};

/**
 * Validate URL format
 */
export const validateUrl = (url: string): ValidationResult => {
  if (!url) {
    return {
      isValid: false,
      errorKey: 'validation.url.required'
    };
  }

  try {
    new URL(url);
    return {
      isValid: true
    };
  } catch (error) {
    return {
      isValid: false,
      errorKey: 'validation.url.invalid'
    };
  }
};
